<template>
  <div class="color-gradient-editor">
    <div class="color-gradient-editor__label">{{ label }}</div>
    <div
      :class="[
        'el-color-picker el-color-picker--mini color-gradient-editor__picker',
        disabled ? 'is-disabled' : ''
      ]"
    >
      <div class="el-color-picker__mask" v-if="disabled"></div>
      <a-dropdown
        :trigger="['click']"
        :disabled="disabled"
        @visibleChange="handleTrigger"
        :visible="showPicker"
        :placement="placement"
      >
        <div class="el-color-picker__trigger">
          <span
            class="el-color-picker__color"
            :class="{ 'is-alpha': showAlpha }"
          >
            <span
              class="el-color-picker__color-inner"
              :style="pickerPreviewStyle"
              v-if="type === 'color'"
            />
            <shape-renderer
              :params="triggerShapeParams"
              :effectId="uniqueShapeId"
              class="el-color-picker__color-inner"
              v-else
            />
            <span
              class="el-color-picker__empty el-icon-close"
              v-if="!value && !showPanelColor"
            />
          </span>
          <span
            class="el-color-picker__icon el-icon-arrow-down"
            v-show="value || showPanelColor"
          />
        </div>

        <pass-current-color
          slot="overlay"
          class="color-gradient-overlay"
          :class="popperClass"
          :currentColor="colorValue"
        >
          <div class="color-gradient-overlay__types" v-show="!hideTypes">
            <div
              class="
                color-gradient-overlay__type color-gradient-overlay__type--color
              "
              :class="{
                'color-gradient-overlay__type--selected': type === 'color'
              }"
              @click="changeType('color')"
            >
              纯色
            </div>
            <div
              class="
                color-gradient-overlay__type
                color-gradient-overlay__type--gradient
              "
              :class="{
                'color-gradient-overlay__type--selected': type === 'gradient'
              }"
              @click="changeType('gradient')"
            >
              渐变
            </div>
          </div>
          <template v-if="type === 'gradient'">
            <div class="color-gradient-overlay__section">
              <label-option
                v-model="editingGradient.type"
                :optionList="typeList"
                label="渐变类型"
              />
              <template v-if="editingGradient.type === 'linear'">
                <!-- TODO: gradient preview. -->
                <div class="color-gradient-editor">
                  <span class="color-gradient-editor__label">
                    渐变角度/比例
                  </span>
                  <div class="color-gradient-editor__start-end">
                    <shape-renderer
                      :params="shapeParams"
                      :effectId="uniqueShapeId + 'copy'"
                      style="margin: -3px"
                    />
                    <div
                      class="color-gradient-stops__stop"
                      :class="{
                        'color-gradient-stops__stop--selected':
                          positionDragging && positionDragging.type === 'start'
                      }"
                      :style="{
                        background: editingGradient.stops[0].color,
                        left: editingGradient.x1 * 100 + '%',
                        top: editingGradient.y1 * 100 + '%',
                        margin: '-7px'
                      }"
                      @mousedown.prevent="
                        handleStartEndMousedown($event, 'start')
                      "
                    />
                    <div
                      class="color-gradient-stops__stop"
                      :class="{
                        'color-gradient-stops__stop--selected':
                          positionDragging && positionDragging.type === 'end'
                      }"
                      :style="{
                        background:
                          editingGradient.stops[
                            editingGradient.stops.length - 1
                          ].color,
                        left: editingGradient.x2 * 100 + '%',
                        top: editingGradient.y2 * 100 + '%',
                        margin: '-7px'
                      }"
                      @mousedown.prevent="
                        handleStartEndMousedown($event, 'end')
                      "
                    />
                  </div>
                </div>
              </template>
              <div class="color-gradient-stops">
                <div
                  class="color-gradient-stops__gradient"
                  :style="gradientStyle"
                >
                  <el-tooltip
                    v-for="(stop, i) in editingGradient.stops"
                    :key="i"
                    :content="stop.offset + '%'"
                    :value="dragging && i === activeStop"
                    manual
                    :open-delay="100"
                    placement="top"
                    ref="tooltip"
                  >
                    <div
                      class="color-gradient-stops__stop"
                      :class="{
                        'color-gradient-stops__stop--selected': i === activeStop
                      }"
                      :style="{
                        background: stop.color,
                        left: stop.offset + '%'
                      }"
                      @mousedown="handleStopMousedown($event, i)"
                    />
                  </el-tooltip>
                </div>
                <a-button
                  class="color-gradient-stops__btn"
                  size="small"
                  @click="addColorStop"
                  icon="plus"
                />
                <a-button
                  class="color-gradient-stops__btn"
                  size="small"
                  @click="removeColorStop"
                  icon="minus"
                  :disabled="editingGradient.stops.length <= 2"
                />
              </div>
            </div>
          </template>
          <sv-panel :color="color" class="color-gradient-overlay__sv" />
          <div class="color-gradient-overlay__sliders">
            <hue-slider :color="color" class="color-gradient-overlay__item" />
            <alpha-slider
              :color="color"
              class="color-gradient-overlay__item"
              v-show="showAlpha"
            />
            <div
              class="
                color-gradient-overlay__preview
                color-gradient-overlay__preview--alpha
              "
            >
              <div
                class="color-gradient-overlay__preview-color"
                :style="{
                  backgroundColor: displayedColor
                }"
              />
            </div>
          </div>
          <div class="color-gradient-overlay__footer">
            <a-input
              class="color-gradient-overlay__input"
              v-model="inputValue"
              @change.native="handleChange"
              @keydown.native.enter="handleChange"
            />
            <predefine-panel
              :color="color"
              :colors="predefineColors"
              class="color-gradient-overlay__predefine"
            />
            <a-button
              @click="showMaterialColor"
              class="color-gradient-overlay__color-btn"
              type="link"
              v-show="!hidePicker"
            >
              <a-icon :component="ColorPickerIcon" />
              素材拾色器…
            </a-button>
            <p class="color-gradient-overlay__btns">
              <a-button class="color-gradient-overlay__btn" @click="clearValue">
                清空
              </a-button>
              <a-button
                type="primary"
                class="color-gradient-overlay__btn"
                @click="done"
              >
                完成
              </a-button>
            </p>
          </div>
        </pass-current-color>
      </a-dropdown>
    </div>
  </div>
</template>

<script>
import Color from 'element-ui/packages/color-picker/src/color';
import Emitter from 'element-ui/src/mixins/emitter';
import AlphaSlider from 'element-ui/packages/color-picker/src/components/alpha-slider.vue';
import HueSlider from 'element-ui/packages/color-picker/src/components/hue-slider.vue';
import SVPanel from 'element-ui/packages/color-picker/src/components/sv-panel.vue';
import Predefine from 'element-ui/packages/color-picker/src/components/predefine.vue';
import * as d3 from 'd3-interpolate';
import LabelOption from './LabelOption';
import { clamp } from '../../utils';
import { PassCurrentColor, getPredefineColors, ColorPickerIcon } from './ColorGradientUtils';
import ShapeRenderer from '../player/ShapeRenderer';

export default {
  name: 'ElColorPicker',

  mixins: [Emitter],

  model: {
    prop: 'value',
    event: 'change'
  },

  components: {
    AlphaSlider,
    HueSlider,
    'sv-panel': SVPanel,
    LabelOption,
    'predefine-panel': Predefine,
    PassCurrentColor,
    'shape-renderer': ShapeRenderer
  },

  props: {
    value: String,
    showAlpha: Boolean,
    colorFormat: String,
    disabled: Boolean,
    size: String,
    popperClass: String,
    predefine: Array,
    label: String,
    gradient: Object,
    placement: String,
    hideTypes: Boolean,
    hidePicker: Boolean
  },

  computed: {
    materialColorVisible() {
      return this.$store && this.$store.state.materialColorVisible;
    },
    materialColor() {
      return this.$store && this.$store.state.materialColor;
    },
    displayedColor() {
      if (!this.value && !this.showPanelColor) {
        return 'transparent';
      }

      return this.displayedRgb(this.color, this.showAlpha);
    },
    colorValue() {
      return this.showPanelColor ? this.color.value : ''
    },

    usedValue() {
      if (this.type === 'color') {
        return this.value;
      }

      return this.editingGradient.stops[this.activeStop].color;
    },
    gradientStyle() {
      if (this.type === 'color') {
        return '';
      }

      return {
        backgroundImage: `linear-gradient(to right,${this.editingGradient.stops.map(s => `${s.color} ${s.offset}%`).join(',')})`
      }
    },
    pickerPreviewStyle() {
      if (this.type === 'color') {
        return {
          backgroundColor: this.displayedColor
        };
      }
      return null;
    },
    currentOffset() {
      if (this.dragging) {
        return this.editingGradient.stops[this.activeStop].offset;
      }

      return 0;
    },
    prevGradient: {
      get() {
        return this.$store && this.$store.state.prevGradients.shape || null;
      },
      set(value) {
        if (this.$store) {
          this.$set(this.$store.state.prevGradients, 'shape', value);
        }
      }
    },
    triggerShapeParams() {
      return {
        shape: 'rect',
        width: 42,
        height: 17,
        stroke: 'transparent',
        strokeWidth: 0,
        color: 'transparent',
        gradient: this.editingGradient
      };
    },
    shapeParams() {
      return {
        shape: 'rect',
        width: 60,
        height: 45,
        stroke: 'transparent',
        strokeWidth: 0,
        color: 'transparent',
        gradient: this.editingGradient,
        radius: 11
      };
    }
  },

  watch: {
    usedValue(val) {
      if (!val) {
        this.showPanelColor = false;
      } else {
        if (val !== this.color.value) {
          this.color.fromString(val);
        }

        this.showPanelColor = true;
      }
    },
    color: {
      deep: true,
      handler() {
        this.showPanelColor = true;

        this.$nextTick(() => {
          if (this.type === 'gradient') {
            this.editingGradient.stops[this.activeStop].color = this.colorValue;
          }
        });
      }
    },
    displayedColor(val) {
      if (!this.showPicker) return;
      const currentValueColor = new Color({
        enableAlpha: this.showAlpha,
        format: this.colorFormat
      });
      currentValueColor.fromString(this.usedValue);

      const currentValueColorRgb = this.displayedRgb(currentValueColor, this.showAlpha);
      if (val !== currentValueColorRgb) {
        this.$emit('active-change', val);
      }
    },

    colorValue() {
      this.inputValue = this.colorValue;
    },
    gradient: {
      deep: true,
      handler(val) {
        if (val) {
          const gradientCopy = JSON.parse(JSON.stringify(val));
          this.type = 'gradient';
          this.color.fromString(val.stops[0].color);
          this.editingGradient = gradientCopy;
        } else {
          this.type = 'color';
          if (this.prevGradient) {
            const gradientCopy = JSON.parse(JSON.stringify(this.prevGradient));
            this.editingGradient = gradientCopy;
          }
        }
      }
    },
    currentOffset() {
      this.$refs.tooltip[this.activeStop].updatePopper();
    },
    materialColor(val) {
      if (this.showPicker) {
        this.inputValue = val;
        this.handleChange();
      }
    }
  },

  methods: {
    handleTrigger(value) {
      if (!this.materialColorVisible) {
        this.showPicker = value;
        if (!value) {
          this.resetColor();
          this.activeStop = 0;
        }
      }
    },
    changeType(type) {
      this.type = type;
    },
    handleStopMousedown(e, i) {
      this.activeStop = i;
      this.dragging = {
        offset: this.editingGradient.stops[i].offset,
        pos: e.clientX
      };

      document.body.classList.add('unselectable');
    },
    handleMousemove(e) {
      if (this.dragging && !this.dragging.stopped) {
        const offset = clamp(
          Math.round((e.clientX - this.dragging.pos) / 144 * 100 + this.dragging.offset), 0, 100);
        this.editingGradient.stops[this.activeStop].offset = offset;
      } else if (this.positionDragging && !this.positionDragging.stopped) {
        const { pos, prevValue, type } = this.positionDragging;
        const offsetX = clamp((e.clientX - pos[0]) / 54 + prevValue[0], 0, 1);
        const offsetY = clamp((e.clientY - pos[1]) / 39 + prevValue[1], 0, 1);

        switch (type) {
          case 'start':
            this.editingGradient.x1 = offsetX;
            this.editingGradient.y1 = offsetY;
            break;
          default:
            this.editingGradient.x2 = offsetX;
            this.editingGradient.y2 = offsetY;
        }
      }
    },
    handleMouseup() {
      if (this.dragging && !this.dragging.stopped) {
        this.dragging.stopped = true;

        setTimeout(() => {
          this.dragging = null;
        }, 400);
      } else if (this.positionDragging) {
        this.positionDragging = false;
      } else { return; }
      setTimeout(() => {
        document.body.classList.remove('unselectable');
      }, 0);
    },
    handleStartEndMousedown(e, type) {
      this.positionDragging = {
        type,
        prevValue: type === 'start'
          ? [this.editingGradient.x1, this.editingGradient.y1]
          : [this.editingGradient.x2, this.editingGradient.y2],
        pos: [e.clientX, e.clientY]
      };

      document.body.classList.add('unselectable');
    },
    addColorStop() {
      const isBeforeLast = this.activeStop < this.editingGradient.stops.length - 1;
      const nextOffset = isBeforeLast
        ? Math.round((this.editingGradient.stops[this.activeStop].offset + this.editingGradient.stops[this.activeStop + 1].offset) / 2)
        : 100;
      const nextColor = isBeforeLast
        ? d3.interpolateRgb(this.editingGradient.stops[this.activeStop].color, this.editingGradient.stops[this.activeStop + 1].color)(.5)
        : this.editingGradient.stops[this.activeStop].color;
      this.editingGradient.stops = [].concat.apply([], this.editingGradient.stops.map((item, i) => {
        if (i === this.activeStop) {
          return [item, { offset: nextOffset, color: nextColor }];
        }
        return item;
      }));
      this.activeStop++;
    },
    removeColorStop() {
      if (this.editingGradient.stops.length > 2) {
        this.editingGradient.stops = this.editingGradient.stops.filter((_, i) => i !== this.activeStop);
        if (this.activeStop > 0) {
          this.activeStop--;
        }
      }
    },
    clearValue() {
      this.$emit('input', null);
      this.$emit('change', null);
      this.showPanelColor = false;
      this.showPicker = false;
      this.resetColor();
    },
    resetColor() {
      // TODO: handle the correct panel
      if (this.gradient) {
        const gradientCopy = JSON.parse(JSON.stringify(this.gradient));
        this.editingGradient = {
          ...gradientCopy,
          type: gradientCopy.type === 'none' ? 'linear' : gradientCopy.type
        };
      }
      this.$nextTick(_ => {
        this.type = this.gradient && this.gradient.type !== 'none' ? 'gradient' : 'color';
        if (this.usedValue) {
          this.color.fromString(this.usedValue);
        } else {
          this.showPanelColor = false;
        }
      });
    },
    displayedRgb(color, showAlpha) {
      if (!(color instanceof Color)) {
        throw Error('color should be instance of Color Class');
      }

      const { r, g, b } = color.toRgb();
      return showAlpha
        ? `rgba(${r}, ${g}, ${b}, ${color.get('alpha') / 100})`
        : `rgb(${r}, ${g}, ${b})`;
    },
    handleChange() {
      this.color.fromString(this.inputValue);
    },
    done() {
      if (this.type === 'color') {
        this.$emit('change', this.colorValue);
        if (this.gradient) {
          this.prevGradient = this.gradient;
          this.$emit('update:gradient', null);
        }
      } else {
        this.$emit('update:gradient', this.editingGradient);
        this.prevGradient = this.editingGradient;
      }
      this.$nextTick(() => {
        this.handleTrigger(false);
      });
    },
    showMaterialColor() {
      this.$store.commit('handleMaterialColorVisible', true);
    }
  },

  mounted() {
    const value = this.value;

    if (this.gradient) {
      const gradientCopy = JSON.parse(JSON.stringify(this.gradient));
      this.type = 'gradient';
      this.color.fromString(this.gradient.stops[0].color);
      this.editingGradient = gradientCopy;
    } else {
      if (value) {
        this.color.fromString(value);
      }

      if (this.prevGradient) {
        this.editingGradient = JSON.parse(JSON.stringify(this.prevGradient));
      }
    }

    this.$nextTick(() => {
      this.inputValue = this.colorValue;
    });

    document.body.addEventListener('mousemove', this.handleMousemove);
    document.body.addEventListener('mouseup', this.handleMouseup);
  },

  beforeDestroy() {
    document.body.removeEventListener('mousemove', this.handleMousemove);
    document.body.removeEventListener('mouseup', this.handleMouseup);
  },

  data() {
    const color = new Color({
      enableAlpha: this.showAlpha,
      format: this.colorFormat
    });

    return {
      color,
      showPicker: false,
      showPanelColor: false,
      type: 'color',
      inputValue: '',
      editingGradient: {
        type: 'linear',
        x1: 0,
        x2: 1,
        y1: 0,
        y2: 0,
        stops: [
          { offset: 0, color: '#007eff' },
          { offset: 100, color: '#ee44cc' }
        ]
      },
      activeStop: 0,
      dragging: null,
      positionDragging: false,
      typeList: [
        { name: '线性渐变', value: 'linear' },
        { name: '径向渐变', value: 'radial' }
      ],
      predefineColors: getPredefineColors(this.showAlpha),
      uniqueShapeId: 'shape' + ('' + Math.random()).slice(2),
      ColorPickerIcon
    };
  }
};
</script>
<style>
.el-color-picker,
.el-color-picker--mini {
  height: 25px;
}
.el-color-picker__trigger {
  padding: 2px;
  border-radius: 2px;
  border: 1px solid rgba(229, 229, 231, 1);
}
.el-color-picker--mini .el-color-picker__trigger {
  height: 25px;
  width: 50px;
}
.el-color-picker__icon {
  display: none;
}

.color-gradient-editor .el-color-picker__mask {
  width: 100%;
  height: 100%;
  margin: -1px;
}

.color-gradient-overlay {
  width: 256px;
  background: #fff;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06), 0 0 8px rgba(0, 0, 0, 0.1);
}

.color-gradient-overlay__types {
  padding: 18px 0;
  display: flex;
  justify-content: center;
}

.color-gradient-overlay__type {
  position: relative;
  padding-left: 36px;
  margin: 0 14px;
  cursor: pointer;
}

.color-gradient-overlay__type--selected {
  color: #007eff;
}

.color-gradient-overlay__type:after {
  content: "";
  position: absolute;
  left: 0;
  top: 50%;
  width: 24px;
  height: 24px;
  border-radius: 12px;
  margin-top: -12px;
  background-color: #fff;
}

.color-gradient-overlay__type--color:after {
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.color-gradient-overlay__type--selected.color-gradient-overlay__type--color:after {
  background: #007eff;
}

.color-gradient-overlay__type--gradient:after {
  background: linear-gradient(to bottom, #333, transparent);
}

.color-gradient-overlay__type--selected.color-gradient-overlay__type--gradient:after {
  background: linear-gradient(to bottom, #007eff, transparent);
}

.color-gradient-overlay__section {
  padding: 0 20px 16px;
  margin-top: -6px;
}

.color-gradient-editor {
  display: flex;
  margin-top: 10px;
  justify-content: space-between;
  align-items: center;
}

.color-gradient-editor__label {
  font-size: 12px;
}

.color-gradient-editor__picker {
  width: 50px;
  margin-left: 3px;
  text-align: center;
  font-size: 12px;
  color: rgba(101, 102, 103, 1);
  height: 25px;
  line-height: 25px;
}

.color-gradient-editor__start-end {
  position: relative;
  border: 7px solid transparent;
  margin-right: -4px;
}

.color-gradient-editor__group {
  width: 100px;
  height: 25px;
}

.color-gradient-editor__group.ant-input-group > :first-child {
  border-top-left-radius: 2px;
  border-bottom-left-radius: 2px;
}

.color-gradient-editor__group.ant-input-group > :last-child {
  border-bottom-right-radius: 2px;
  border-top-right-radius: 2px;
}

.color-gradient-editor__input {
  width: 50%;
  height: 100%;
  font-size: 12px;
  line-height: 25px;
  border-color: #e5e5e7;
}

.color-gradient-editor__input .ant-input-number-input {
  height: 25px;
}

.color-gradient-stops {
  display: flex;
  align-items: center;
  margin-top: 12px;
}

.color-gradient-stops__gradient {
  height: 12px;
  border-radius: 9px;
  flex-grow: 1;
  margin-right: 8px;
  position: relative;
  z-index: 0;
}

.color-gradient-stops__stop {
  position: absolute;
  top: 0;
  left: 0;
  width: 14px;
  height: 14px;
  border-radius: 9px;
  margin: -1px -7px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06), 0 0 8px rgba(0, 0, 0, 0.15);
  transition: box-shadow 0.3s, border-color 0.3s, transform 0.3s;
  border: 3px solid #fff;
  outline: 0;
}

.color-gradient-stops__stop:hover {
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06), 0 0 8px rgba(0, 0, 0, 0.15),
    0 6px 20px rgba(0, 0, 0, 0.08);
  transform: scale(1.25);
}

.color-gradient-stops__stop:active {
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06), 0 0 8px rgba(0, 0, 0, 0.15);
  border-color: #f2f2f2;
}

.color-gradient-stops__stop--selected {
  transform: scale(1.25);
}

.color-gradient-stops__stop--selected:hover {
  box-shadow: 0 0 0 3px rgba(0, 127, 255, 0.1), 0 0 8px rgba(0, 0, 0, 0.15),
    0 6px 20px rgba(0, 0, 0, 0.08);
}

.color-gradient-stops__stop--selected,
.color-gradient-stops__stop--selected:active {
  box-shadow: 0 0 0 3px rgba(0, 127, 255, 0.1), 0 0 8px rgba(0, 0, 0, 0.15),
    0 0 0 rgba(0, 0, 0, 0.08);
}

.color-gradient-stops__btn {
  margin-left: 8px;
  border-color: #e5e5e7;
}

.color-gradient-stops__btn:disabled {
  opacity: 0.3;
}

.color-gradient-stops__btn.ant-btn-sm {
  border-radius: 2px;
  font-size: 12px;
  line-height: 1;
}

.color-gradient-overlay__sliders {
  padding: 8px 60px 8px 20px;
  position: relative;
}

.color-gradient-overlay__preview {
  position: absolute;
  right: 20px;
  top: 50%;
  margin-top: -18px;
  width: 24px;
  height: 24px;
  border-radius: 12px;
}

.color-gradient-overlay__preview--alpha {
  background: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAwAAAAMCAIAAADZF8uwAAAAGUlEQVQYV2M4gwH+YwCGIasIUwhT25BVBADtzYNYrHvv4gAAAABJRU5ErkJggg==);
}

.color-gradient-overlay__preview-color {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 12px;
  box-shadow: inset 0 0 0 2px rgba(0, 0, 0, 0.06);
}

.color-gradient-overlay__sv,
.color-gradient-overlay__item {
  width: 100%;
  margin-bottom: 12px;
}

.color-gradient-overlay__sv {
  height: 150px;
}

.color-gradient-overlay__sv .el-color-svpanel__cursor > div {
  width: 8px;
  height: 8px;
  transform: translate(-4px, -4px);
  box-shadow: 0 0 0 3px #fff, inset 0 0 1px 1px rgba(0, 0, 0, 0.1),
    0 0 1px 4px rgba(0, 0, 0, 0.15);
}

.color-gradient-overlay__item,
.color-gradient-overlay__item .el-color-hue-slider__bar,
.color-gradient-overlay__item .el-color-alpha-slider__bar {
  border-radius: 9px;
}

.color-gradient-overlay__item .el-color-hue-slider__thumb,
.color-gradient-overlay__item .el-color-alpha-slider__thumb {
  background: transparent;
  box-shadow: none;
  border: 0;
}

.color-gradient-overlay__item .el-color-hue-slider__thumb:after,
.color-gradient-overlay__item .el-color-alpha-slider__thumb:after {
  background: #fff;
  content: "";
  position: absolute;
  left: -5px;
  top: -1px;
  border-radius: 9px;
  width: 14px;
  height: 14px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05), 0 0 8px rgba(0, 0, 0, 0.15);
  transition: 0.3s;
}

.color-gradient-overlay__item .el-color-hue-slider__thumb:hover:after,
.color-gradient-overlay__item .el-color-alpha-slider__thumb:hover:after {
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05), 0 0 8px rgba(0, 0, 0, 0.15),
    0 6px 20px rgba(0, 0, 0, 0.08);
}

.color-gradient-overlay__item .el-color-hue-slider__thumb:active:after,
.color-gradient-overlay__item .el-color-alpha-slider__thumb:active:after {
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05), 0 0 8px rgba(0, 0, 0, 0.1),
    0 6px 20px transparent, inset 0 2px 6px rgba(0, 0, 0, 0.1);
}

.color-gradient-overlay__footer {
  padding: 0 20px 20px;
}

.color-gradient-overlay__input {
  height: 28px;
  display: block;
  border-radius: 2px;
  line-height: 26px;
  margin-bottom: 16px;
  border-color: #e5e5e7;
}

.color-gradient-overlay__predefine {
  width: 100%;
  margin: 8px 0;
}

.color-gradient-overlay__predefine .el-color-predefine__color-selector {
  margin-left: 8px;
  border-radius: 99px;
  transition: box-shadow 0.3s;
}

.color-gradient-overlay__predefine
  .el-color-predefine__color-selector.selected {
  box-shadow: 0 0 0 4px rgba(0, 127, 255, 0.1);
}

.color-gradient-overlay__predefine .el-color-predefine__color-selector > div {
  border-radius: 99px;
  box-shadow: inset 0 0 0 2px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.3s;
}

.color-gradient-overlay__predefine
  .el-color-predefine__color-selector.selected
  > div {
  box-shadow: none;
}

.color-gradient-overlay__predefine
  .el-color-predefine__color-selector:nth-child(8n + 1) {
  margin-left: 0;
}

.color-gradient-overlay__btns {
  margin: 0;
  display: flex;
  justify-content: center;
}

.color-gradient-overlay__btn {
  margin: 0 10px;
  height: 28px;
  border-radius: 14px;
}

.color-gradient-overlay__color-btn {
  padding: 0;
  margin: 0 0 14px;
  display: block;
  height: 24px;
}

.color-gradient-overlay__color-btn .anticon {
  font-size: 16px;
}
</style>
